import { recipe } from "@vanilla-extract/recipes";
import { style } from "@vanilla-extract/css";
import { rem } from "@huub-web/utils/css/rem";

import { theme } from "../../theme/css/theme.css";
import { zIndexes } from "../../theme/zIndex";
import { responsive } from "../../utils/responsive";
import { transition } from "../../utils/transition";
import { disabledState } from "../../theme/disabledState";

export const containerStyle = recipe({
  base: {
    color: theme.colors.onBackground,
  },
  variants: {
    variant: {
      canvas: {
        position: "fixed",
        bottom: 0,
        zIndex: zIndexes.taskBar,
        width: "100%",
        paddingBottom: theme.spacings.xs,
      },
      sideContent: {
        zIndex: 3,
        position: "relative",
        width: "100%",
        padding: `0 ${theme.spacings.xs} ${theme.spacings.xs} ${theme.spacings.xs}`,
      },
    },
  },
  defaultVariants: {
    variant: "canvas",
  },
});

export const taskbarStyle = recipe({
  base: {
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    flexGrow: 0,
    justifyContent: "center",
    border: "1px solid transparent",
    cursor: "text",
    transition: transition(
      "color",
      "outline",
      "background-color",
      "border-color",
      "outline-color",
    ),
    borderRadius: theme.borderRadiuses.onS,
    width: "100%",
    minHeight: rem(56),
    padding: theme.spacings.xs,
    overflow: "hidden",
    gap: theme.spacings.xs,
  },
  variants: {
    size: {
      default: {},
    },
    variant: {
      canvas: {
        margin: "auto",
        backgroundColor: theme.colors.background,
        borderColor: theme.colors.defaultBorder,
        color: theme.colors.onSecondary,
        outline: "1px solid transparent",
        vars: {
          "--focus-border-color": theme.colors.info,
          "--focus-background-color": theme.colors.background,
          "--focus-outline-color": "transparent",
        },
      },
      sideContent: {
        backgroundColor: theme.colors.background,
        borderColor: theme.colors.background,
        color: theme.colors.onSecondary,
        outline: "1px solid transparent",
        vars: {
          "--focus-border-color": theme.colors.primary,
          "--focus-background-color": theme.colors.background,
          "--focus-outline-color": "transparent",
        },
      },
    },
    focused: {
      true: {
        backgroundColor: "var(--focus-background-color)",
        borderColor: "var(--focus-border-color)",
        outlineColor: "var(--focus-outline-color)",
      },
    },
    disabled: {
      true: {
        ...disabledState,
        opacity: "0.5 !important",
      },
    },
  },
  defaultVariants: {
    size: "default",
    variant: "canvas",
  },
});

export const textAreaStyle = style({
  padding: theme.spacings.xxs,
  flexGrow: 0,
});

export const edgeContentStyle = recipe({
  base: {
    alignSelf: "center",
  },
});

export const actionsStyle = recipe({
  base: {
    padding: theme.spacings.xxs,
  },
});

export const messagesStackStyle = recipe({
  base: {
    position: "absolute",
    pointerEvents: "none",
    height: "auto",
    bottom: "100%",
    left: 0,
    right: 0,
    zIndex: 2,
    selectors: {
      "&:empty": {
        display: "none",
      },
      "&::-webkit-scrollbar": {
        display: "none",
      },
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: rem(10),
        height: "100%",
        background: `linear-gradient(to right, var(--background-color), transparent)`,
        zIndex: 3,
        pointerEvents: "none",
      },
      "&:after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        width: rem(10),
        height: "100%",
        background: `linear-gradient(to right, transparent, var(--background-color))`,
        zIndex: 3,
        pointerEvents: "none",
      },
    },
  },
  variants: {
    variant: {
      canvas: {
        vars: {
          "--background-color": theme.colors.background,
        },
      },
      sideContent: {
        vars: {
          "--background-color": theme.colors.panel,
        },
      },
    },
  },
});

export const suggestionsOverflowStyle = recipe({
  base: {
    position: "relative",
    overflowX: "auto",
    textAlign: "center",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    display: "block",
    alignItems: "flex-end",
    justifyContent: "center",
    width: "calc(100% - 2px)",
  },
  variants: {
    variant: {
      canvas: {
        padding: `0 0 ${theme.spacings.xs}`,
      },
      sideContent: {
        padding: `0 ${theme.spacings.s} 0 ${theme.spacings.xs}`,
      },
    },
  },
  defaultVariants: {
    variant: "canvas",
  },
});

export const suggestionsFlexStyle = recipe({
  base: {
    pointerEvents: "auto",
    ":empty": {
      display: "none",
    },
  },
  variants: {
    variant: {
      canvas: {
        padding: `0 ${theme.spacings.s}`,
        ...responsive({
          base: {
            gap: theme.spacings.xxs,
          },
          s: {
            gap: theme.spacings.xs,
          },
        }),
      },
      sideContent: {
        padding: `${theme.spacings.s} 0 ${theme.spacings.xs} 0`,
        ...responsive({
          base: {
            gap: theme.spacings.xxs,
          },
          s: {
            gap: theme.spacings.xs,
          },
        }),
      },
    },
  },
});

export const userMessageFlexStyle = style({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  height: "100%",
});

export const userMessageContainerStyle = style({
  alignContent: "flex-end",
  marginTop: 0,
  marginBottom: 0,
});
